'use client';

import * as React from 'react';
import { Menu } from 'lucide-react';

import { cn } from '@/components/atoms/primitives/utils';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/atoms/primitives/sheet';
import { HELP_LEGAL_PATHS } from '@/lib/nav/helpLegalLinks';
import { apexUrl } from '@/lib/cross-host-urls';

/**
 * HamburgerDrawer — help/legal sheet for the **apex** and **studio** surfaces
 * only (WEB-628 disposition).
 *
 * Why it is NOT mounted on `AppShellLayout` / `AppHeader`: the signed-in app
 * surface uses `AppNavMenu` — the app host for the shared `NavMenuSheet` shell
 * (WEB-613 → WEB-629) with `AppMenuSheetContent` (WEB-628) — which already
 * absorbs this help/legal set plus product secondary nav. Mounting this drawer
 * there would recreate three drawer-ish things on three surfaces. Apex + studio
 * keep this lighter Sheet until those surfaces adopt `NavMenuSheet`.
 *
 * All links resolve to the apex host (`loci.city/*`) via `apexUrl()`, so this
 * component is host-agnostic. Uses `<a>` rather than `next/link` because every
 * link is cross-host.
 */
export interface HamburgerDrawerProps {
  className?: string;
}

export function HamburgerDrawer({ className }: HamburgerDrawerProps): React.ReactElement {
  return (
    <Sheet>
      <SheetTrigger
        aria-label="Open menu"
        className={cn(
          'inline-flex size-11 desktop:size-9 min-h-11 desktop:min-h-0 min-w-11 desktop:min-w-0 items-center justify-center rounded-md text-foreground hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          className,
        )}
      >
        <Menu className="size-5" aria-hidden="true" />
      </SheetTrigger>
      <SheetContent side="left" className="w-72" title={null}>
        <SheetHeader>
          <SheetTitle>Menu</SheetTitle>
        </SheetHeader>
        <nav aria-label="Help and legal">
          <ul className="flex flex-col gap-1 px-2 pb-4">
            {HELP_LEGAL_PATHS.map(({ label, path }) => (
              <li key={path}>
                <a
                  href={apexUrl(path)}
                  className="block rounded-md px-3 py-3 text-sm font-medium text-foreground hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
                >
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </SheetContent>
    </Sheet>
  );
}
